import { ref } from 'vue';
import { SupportedExportFormat } from '@/services/api/resources/analytics';
import { useTask } from '@/ui/concerns';
import {
  exportReport as exportSingleReport,
  publicExportReport as publicExportSingleReport,
  exportWidget as exportSingleWidget,
  bulkExportReport as exportMultipleReports,
  exportDetailedView as exportSingleDetailedView,
} from '@/services/api/resources/analytics-builder/actions';
import { StatusCodes } from 'http-status-codes';
import { containsAttachment, extractFilenameFromDisposition } from '@/ui/helpers';
import { saveFileFromResponse } from '@/ui/helpers/fileHelper';
import { v4 as uuidv4 } from 'uuid';
import { DetailedViewPayload } from '@/ui/concerns/analytics/useDetailedView';

export const useExports = () => {
  const isReportExportLoading = ref(false);
  const isBulkReportExportLoading = ref(false);
  const isWidgetExportLoading = ref(false);
  const isDetailedViewExportLoading = ref(false);

  const saveFile = async (response: Response) => {
    if (response.status !== StatusCodes.OK) {
      console.error(response.status);
      return;
    }

    if (!containsAttachment(response)) {
      console.error('no file');
      return;
    }

    const filename = extractFilenameFromDisposition(response.headers.get('Content-Disposition')!);

    if (!filename) {
      console.error('no filename');
      return;
    }

    await saveFileFromResponse(response, filename);
  };

  const exportReportFn = (id: number, format: SupportedExportFormat, action: Function) => useTask(
    () => {
      isReportExportLoading.value = true;
      return action(id, format);
    },
    async (response: Response) => {
      isReportExportLoading.value = false;
      saveFile(response).catch(e => console.error(e));
    },
    uuidv4(),
    e => {
      console.error(e);
      isReportExportLoading.value = false;
    },
  );

  const exportReport = (id: number, format: SupportedExportFormat) =>
    exportReportFn(id, format, exportSingleReport);

  const publicExportReport = (id: number, format: SupportedExportFormat) =>
    exportReportFn(id, format, publicExportSingleReport);

  const bulkExportReport = (records: number[], format: SupportedExportFormat) => {
    return useTask(
      () => {
        isBulkReportExportLoading.value = true;
        return exportMultipleReports(records, format);
      },
      async (response: Response) => {
        isBulkReportExportLoading.value = false;
        saveFile(response).catch(e => console.error(e));
      },
      uuidv4(),
      e => {
        console.error(e);
        isBulkReportExportLoading.value = false;
      },
    );
  };

  const exportWidget = (id: number, format: SupportedExportFormat) => {
    return useTask(
      () => {
        isWidgetExportLoading.value = true;
        return exportSingleWidget(id, format);
      },
      async (response: Response) => {
        isWidgetExportLoading.value = false;
        saveFile(response).catch(e => console.error(e));
      },
      uuidv4(),
      e => {
        console.error(e);
        isWidgetExportLoading.value = false;
      },
    );
  };

  const exportDetailedView = (payload: DetailedViewPayload, format: SupportedExportFormat, timezone: string) => {
    return useTask(
      () => {
        isDetailedViewExportLoading.value = true;
        return exportSingleDetailedView({
          detailedView: true,
          filters: payload.filters,
          metric: payload.metric,
          attribute: payload.attribute,
          display: null,
          lineChartTimeGrouping: null,
          searchTerm: payload.searchTerm,
        }, format, timezone);
      },
      async (response: Response) => {
        isDetailedViewExportLoading.value = false;
        saveFile(response).catch(e => console.error(e));
      },
      uuidv4(),
      e => {
        console.error(e);
        isDetailedViewExportLoading.value = false;
      },
    );
  };

  return {
    isReportExportLoading,
    isBulkReportExportLoading,
    isWidgetExportLoading,
    isDetailedViewExportLoading,
    exportReport,
    publicExportReport,
    bulkExportReport,
    exportWidget,
    exportDetailedView,
  };
};
